import { useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { Select } from '@utilities';

import styles from './_index.module.scss';

const SortControls = ({ compareByGroups, compareByValue, isDisabled }) => {
  const isOverall = compareByValue === 'overall';

  const [sortOrderValue, setSortOrderValue] = useState('default');
  const [sortByValue, setSortByValue] = useState('default');

  const SORT_ORDER_OPTIONS = [
    { label: 'Default', value: 'default' },
    { label: 'Ascending (A-Z)', value: 'asc' },
    { label: 'Descending (Z-A)', value: 'desc' },
  ];

  const sortByOptions = useMemo(() => {
    const baseOptions = [{ label: 'Default', value: 'default' }];

    if (!compareByGroups?.length) return baseOptions;

    return [
      ...baseOptions,
      ...compareByGroups.map(({ id, name }) => ({
        label: name,
        value: String(id),
      })),
    ];
  }, [compareByGroups]);

  const sortBySelectedOption = useMemo(
    () => sortByOptions.find((opt) => opt.value === sortByValue) ?? sortByOptions?.[0] ?? null,
    [sortByOptions, sortByValue]
  );

  const sortOrderOptions = useMemo(
    () =>
      isOverall ? SORT_ORDER_OPTIONS : SORT_ORDER_OPTIONS.filter((opt) => opt.value !== 'default'),
    [isOverall]
  );

  const sortOrderSelectedOption = useMemo(
    () =>
      sortOrderOptions.find((opt) => opt.value === sortOrderValue) ?? sortOrderOptions?.[0] ?? null,
    [sortOrderOptions, sortOrderValue]
  );

  const showSortOrder = isOverall || sortByValue !== 'default';
  const canClearSortBy = !isOverall && sortByValue !== 'default';

  return (
    <div className={styles['sort-controls-row']}>
      {!isOverall && !isDisabled && (
        <div className={styles['sort-controls-group']}>
          <div className={styles['sort-controls-select-wrapper']}>
            <Select
              className={styles['sort-controls-select']}
              isClearable={canClearSortBy}
              isDisabled={isDisabled}
              isLoading={false}
              label="SORT BY"
              onChange={(opt) => {
                if (!opt) {
                  setSortByValue('default');
                  setSortOrderValue('default');
                  return;
                }
                setSortByValue(opt.value);
              }}
              options={sortByOptions}
              value={sortBySelectedOption}
            />
          </div>
        </div>
      )}

      {!isDisabled && showSortOrder && (
        <div className={styles['sort-controls-group']}>
          <div className={styles['sort-controls-select-wrapper']}>
            <Select
              className={styles['sort-controls-select']}
              isDisabled={isDisabled}
              isLoading={false}
              label="SORT ORDER"
              onChange={(opt) => setSortOrderValue(opt?.value ?? 'default')}
              options={sortOrderOptions}
              value={sortOrderSelectedOption}
            />
          </div>
        </div>
      )}
    </div>
  );
};

SortControls.defaultProps = {
  compareByGroups: [],
  compareByValue: 'overall',
  isDisabled: false,
};

SortControls.propTypes = {
  compareByGroups: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  compareByValue: PropTypes.string,
  isDisabled: PropTypes.bool,
};

export default SortControls;
